import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import './ContractEmails.css';
import { getContractApiUrl, getGmailApiUrl } from '../utils/apiUrls';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();
const GMAIL_URL = getGmailApiUrl();

const ContractEmails = ({ contractId, userEmail, isExpanded = false, onToggle }) => {
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedBodies, setExpandedBodies] = useState(new Set());
  const [totalFound, setTotalFound] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [containerRef, setContainerRef] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    console.log('🔄 ContractEmails useEffect triggered:', { contractId, userEmail });
    if (contractId && userEmail) {
      console.log('✅ Both contractId and userEmail are present, fetching emails...');
      fetchContractEmails();
    } else {
      console.log('❌ Missing required props:', { contractId, userEmail });
    }
  }, [contractId, userEmail]);

  // Filter emails based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEmails(emails);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = emails.filter(email => {
        const searchableText = [
          email.from || '',
          email.from_email || '',
          email.to || '',
          email.cc || '',
          email.subject || '',
          email.body || ''
        ].join(' ').toLowerCase();

        return searchableText.includes(query);
      });
      setFilteredEmails(filtered);
    }
  }, [emails, searchQuery]);

  const fetchContractEmails = async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setOffset(0);
        setEmails([]);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const currentOffset = reset ? 0 : offset;
      console.log('🔍 Fetching emails for contract:', contractId, 'user:', userEmail, 'offset:', currentOffset);

      // Query the Gmail monitoring app for emails related to this contract
      const response = await axios.get(`${GMAIL_URL}/emails`, {
        params: {
          user_email: userEmail,
          contract_id: contractId,
          limit: 10, // Load 10 emails at a time
          offset: currentOffset
        }
      });

      console.log('📧 API Response:', response);
      console.log('📧 Response data:', response.data);
      console.log('📧 Response status:', response.data.status);
      console.log('📧 Emails array:', response.data.emails);

      if (response.data.status === 'success') {
        console.log('✅ Setting emails:', response.data.emails);
        if (reset) {
          setEmails(response.data.emails);
          setFilteredEmails(response.data.emails);
        } else {
          const newEmails = [...emails, ...response.data.emails];
          setEmails(newEmails);
          // Apply current search filter to new emails
          if (!searchQuery.trim()) {
            setFilteredEmails(newEmails);
          } else {
            const query = searchQuery.toLowerCase();
            const filtered = newEmails.filter(email => {
              const searchableText = [
                email.from || '',
                email.from_email || '',
                email.to || '',
                email.cc || '',
                email.subject || '',
                email.body || ''
              ].join(' ').toLowerCase();

              return searchableText.includes(query);
            });
            setFilteredEmails(filtered);
          }
        }
        setTotalFound(response.data.total_found || response.data.total_accessible || 0);
        setHasMore(response.data.has_more || false);
        setOffset(currentOffset + response.data.emails.length);
      } else {
        console.log('❌ API returned non-success status:', response.data.status);
        setError('Failed to fetch emails');
      }
    } catch (err) {
      console.error('Error fetching contract emails:', err);

      if (err.code === 'ERR_NETWORK') {
        setError('Unable to connect to email service. Please ensure the Gmail monitoring app is running.');
      } else if (err.response?.status === 404) {
        setError('No emails found for this contract.');
      } else if (err.response?.status === 400) {
        setError('Invalid request. Please check your parameters.');
      } else if (err.response?.status === 403) {
        setError('You do not have access to this transaction.');
        setEmails([]);
        setFilteredEmails([]);
      } else {
        setError('Failed to load emails. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      // Confirmed: toLocaleDateString uses the browser's local timezone default
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const toggleBodyExpansion = (emailId) => {
    setExpandedBodies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const isBodyExpanded = (emailId) => {
    return expandedBodies.has(emailId);
  };

  const shouldTruncateBody = (body) => {
    return body && body.length > 200;
  };

  const loadMoreEmails = () => {
    if (hasMore && !loadingMore) {
      fetchContractEmails(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user is 80% down the scroll
    if (scrollPercentage > 0.8 && hasMore && !loadingMore) {
      loadMoreEmails();
    }
  };

  const getEmailFilename = (email) => {
    if (email.email_filename) {
      return email.email_filename.split('/').pop();
    }
    return email.message_id || 'Unknown';
  };

  const renderSearch = () => (
    <div className="search-container" onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        placeholder="Search emails..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="search-input"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="emails-section">
        <SectionHeader
          title="Email History"
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
        <div className="emails-content">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emails-section">
        <SectionHeader
          title="Email History"
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
        <div className="emails-content">
          <div className="emails-error">
            <p>{error}</p>
            <button onClick={fetchContractEmails} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="emails-section">
      <SectionHeader
        title="Email History"
        isExpanded={isExpanded}
        onToggle={onToggle}
      >
        {renderSearch()}
      </SectionHeader>

      {/* Removed .section-collapsed to match Key Milestones behavior */}

      {isExpanded && (
        <div className="emails-content">
          {emails.length === 0 ? (
            <div className="no-emails">
              <p>No emails found for this contract.</p>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="no-emails">
              <p>No emails match your search criteria.</p>
            </div>
          ) : (
            <div
              className="emails-list"
              ref={setContainerRef}
              onScroll={handleScroll}
              style={{ maxHeight: '600px', overflowY: 'auto' }}
            >
              {filteredEmails.map((email, index) => {
                const emailId = email.db_id || email.id || `email-${index}`;
                const shouldTruncate = shouldTruncateBody(email.body);
                const isExpanded = isBodyExpanded(emailId);

                return (
                  <div key={emailId} className="email-item">
                    <div className="email-header">
                      <div className="email-sender">
                        {email.from && email.from !== 'N/A' && email.from !== null ? (
                          <>
                            <span className="from-label">From:</span> <strong>{email.from}</strong> {email.from_email}
                          </>
                        ) : (
                          <>
                            <span className="from-label">From:</span> {email.from_email}
                          </>
                        )}
                      </div>
                      <div className="email-date">
                        {formatDate(email.timestamp)}
                      </div>
                    </div>

                    <div className="email-to">
                      {email.to && email.to !== 'N/A' && email.to !== null ? (
                        <>
                          <span className="to-label">To:</span> {email.to} {email.to_email}
                        </>
                      ) : (
                        <>
                          <span className="to-label">To:</span> {email.to_email}
                        </>
                      )}
                      {email.cc && (
                        <span className="cc-info"> | CC: {email.cc}</span>
                      )}
                    </div>

                    <div className="email-subject">
                      <strong>{email.subject || 'No Subject'}</strong>
                    </div>

                    {email.body && (
                      <div
                        className="email-body"
                        onDoubleClick={() => shouldTruncate ? toggleBodyExpansion(emailId) : null}
                        style={{
                          cursor: shouldTruncate ? 'pointer' : 'default',
                          position: 'relative'
                        }}
                      >
                        {shouldTruncate && !isExpanded ?
                          truncateText(email.body, 200) :
                          email.body
                        }
                        {shouldTruncate && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: isExpanded ? '#28a745' : '#6c757d',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}>
                            {isExpanded ? 'EXPANDED' : 'DOUBLE-CLICK TO EXPAND'}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Removed email-filename footer */}
                  </div>
                );
              })}

              {loadingMore && (
                <div className="loading-more">
                  <LoadingSpinner />
                  <span>Loading more emails...</span>
                </div>
              )}

              {hasMore && !loadingMore && (
                <div className="load-more-container">
                  <button
                    className="load-more-button"
                    onClick={loadMoreEmails}
                  >
                    Load More Emails
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContractEmails;